import { combine, Val } from "value-enhancer";
import { IServiceVideoChatAvatar } from "@obsbot/remo-services";
import { SideEffectManager } from "side-effect-manager";
import { TencentRTCWeb } from "./tencent-rtc-web";
import { StartLocalVideoOptions } from "./typing";
import { LocalAudioConfig, TRTCStreamType } from "trtc-sdk-v5";

export interface RTCAvatarConfig {
    rtc: TencentRTCWeb;
    element?: HTMLElement | null;
}

export class RTCLocalAvatar implements IServiceVideoChatAvatar {
    private readonly _rtc: TencentRTCWeb;
    private readonly _sideEffect = new SideEffectManager();

    private readonly _shouldCamera$ = new Val(false);
    private readonly _shouldMic$ = new Val(false);
    private readonly _cameraId$ = new Val("");
    private readonly _micId$ = new Val("");
    private readonly _isFrontCamera$ = new Val(true);
    public isStartAudio = false;

    private readonly _el$: Val<HTMLElement | undefined | null>;

    public constructor(config: RTCAvatarConfig) {
        this._rtc = config.rtc;
        this._el$ = new Val(config.element);
        this._sideEffect.addDisposer(
            this._shouldCamera$.subscribe(enabled => {
                try {
                    const el = this._el$.value;
                    if (el && enabled) {
                        const config: StartLocalVideoOptions = {
                            useFrontCamera: this._isFrontCamera$.value,
                            mirror: "both",
                            profile: "480p",
                        };
                        config.cameraId = this._rtc.getCameraID();
                        this.enableLocalVideo(true, el, config);
                    } else {
                        this.enableLocalVideo(false);
                    }
                } catch (error) {
                    console.error(error);
                }
            }),
        );
        this._sideEffect.addDisposer(
            this._shouldMic$.subscribe(shouldMic => {
                try {
                    if (shouldMic) {
                        const config: LocalAudioConfig = {
                            option: {
                                captureVolume: this._rtc.captureVolume,
                            },
                        };
                        if (this._micId$.value) {
                            (config.option ??= {}).microphoneId = this._rtc.getMicID();
                        }
                        this.isStartAudio = true;
                        this._rtc.rtc.startLocalAudio(config);
                    } else {
                        this._rtc.rtc.stopLocalAudio();
                    }
                } catch (error) {
                    console.error(error);
                }
            }),
        );
        this._sideEffect.addDisposer(
            combine([this._cameraId$]).subscribe(([_]) => {
                try {
                    const el = this._el$.value;
                    if (el && this._shouldCamera$.value) {
                        const config: StartLocalVideoOptions = {
                            mirror: this._rtc.isVideoMirror ? "both" : false,
                            profile: "480p",
                        };
                        config.cameraId = this._rtc.getCameraID();
                        this.enableLocalVideo(true, el, config);
                    }
                } catch (error) {
                    console.error(error);
                }
            }),
        );
    }
    public setCameraId(cameraId: string): void {
        this._cameraId$.setValue(cameraId);
    }
    public setMicId(micId: string): void {
        this._micId$.setValue(micId);
    }
    public setIsFrontCamera(isFrontCamera: boolean): void {
        this._isFrontCamera$.setValue(isFrontCamera);
    }
    private enableLocalVideo(
        enabled: boolean,
        el?: HTMLElement,
        config?: StartLocalVideoOptions,
    ): void {
        const rtc = this._rtc.rtc;
        if (enabled) {
            rtc.startLocalVideo({ view: el, option: { ...config } });
        } else {
            rtc.stopLocalVideo();
        }
    }

    public enableCamera(enabled: boolean): void {
        this._shouldCamera$.setValue(enabled);
    }
    public enableMic(enabled: boolean): void {
        this._shouldMic$.setValue(enabled);
    }
    public setElement(element: HTMLElement | null): void {
        this._el$.setValue(element);
    }
    public updateElementView(element: HTMLElement | null): void {
        const handleUpdateElementView = async (): Promise<void> => {
            this._el$.setValue(element);
            if (this._shouldCamera$.value) {
                try {
                    await this._rtc.rtc.updateLocalVideo({
                        view: element,
                        option: {
                            mirror: this._rtc.isVideoMirror ? "both" : false,
                            profile: "480p",
                        },
                    });
                } catch (error) {
                    if (error.message.includes("not started")) {
                        this._rtc.rtc.startLocalVideo({
                            view: element,
                            option: {
                                mirror: this._rtc.isVideoMirror ? "both" : false,
                                profile: "480p",
                                cameraId: this._rtc.getCameraID(),
                            },
                        });
                    }
                }
            } else {
                this.enableLocalVideo(false);
            }
        };
        handleUpdateElementView();
    }
    public getVolumeLevel(): number {
        return this._rtc.getVolumeLevel() || 0;
    }
    public destroy(): void {
        this._sideEffect.flushAll();
    }
    public enableRemoteAudio(enabled: boolean): void {
        this._shouldMic$.setValue(enabled);
    }
    public getVideoTrack(processed?: boolean): MediaStreamTrack | null {
        return this._rtc.rtc.getVideoTrack({
            userId: "",
            streamType: TRTCStreamType.Main,
            processed,
        });
    }
    public getAudioTrack(processed?: boolean): MediaStreamTrack | null {
        return this._rtc.rtc.getAudioTrack({
            userId: "",
            streamType: TRTCStreamType.Main,
            processed,
        });
    }

    public refreshAvatar(): void {
        const el = this._el$.value;
        const shouldCamera = this._shouldCamera$.value;
        const shouldMic = this._shouldMic$.value;
        console.log("[TRTC] [WEB] [LOG] refreshAvatar是否播放本地流", el, shouldCamera);
        if (el) {
            const config: StartLocalVideoOptions = {
                useFrontCamera: this._isFrontCamera$.value,
                mirror: "both",
                profile: "480p",
            };
            config.cameraId = this._rtc.getCameraID();
            this.enableLocalVideo(shouldCamera, el, config);
        } else {
            this.enableLocalVideo(false);
        }
        if (shouldMic) {
            const config: LocalAudioConfig = {
                option: {
                    captureVolume: this._rtc.captureVolume,
                },
            };
            if (this._micId$.value) {
                (config.option ??= {}).microphoneId = this._rtc.getMicID();
            }
            this.isStartAudio = true;
            this._rtc.rtc.startLocalAudio(config);
        } else {
            this._rtc.rtc.stopLocalAudio();
        }
    }
    public hasEl(): boolean {
        return !!this._el$.value;
    }
}
